import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Product } from 'graphql-api/gql-api-interfaces';
import * as yaml from 'js-yaml';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import {
  IFile,
  IFileDirectory,
  IFooter,
  IInformationBannerConfiguration,
  ILandingPageMerchandise,
  ISecondaryNavigationBarConfig,
  MediaAsset,
  MerchandiseMarketProduct,
  MerchandisePrices,
  MerchandiseSlotId,
  MerchandisingContext,
  TextAsset,
} from '../interfaces';
import { ILiveStreamConfiguration } from '../interfaces/live-stream-configuration';
import { MERCHANDISE_API_TOKEN } from '../token';

const START_FROM = 0;
const PAGE_SIZE = 25;
const DEFAULT_HEADERS_PARAMS = {
  'Cache-Control': 'max-age=0, must-revalidate',
};

interface MerchandiseHTMLResponseEntry<T> {
  html: T;
}

export enum HomePageContent {
  LANDING_PAGE = 'landing-page',
  LANDING_PAGE_PANEL = 'landing-page-panel',
  NEWS_FEED = 'news-feed',
  NEWS_FEED_MASONRY = 'news-feed-masonry',
  LANDING_PAGE_STORY = 'landing-page-story',
  LANDING_PAGE_POWER_LIST = 'landing-page-power-list',
  BUILDER_CONTENT = 'builder-content',
  PULSE_CONTENT = 'content',
}

@Injectable()
export class MerchandiseService {
  constructor(
    private http: HttpClient,
    @Inject(MERCHANDISE_API_TOKEN) private apiUrl: string,
  ) {}

  public getSiteSelectorBackground(accountId: string) {
    return this.http.get<string>(`${this.apiUrl}/site-selector-background`, {
      params: getMerchandiseParams(accountId),
    });
  }

  public getLoginBackground() {
    return this.http
      .get<{ html: string }[]>(`${this.apiUrl}/login-page`)
      .pipe(map((result) => result[0].html));
  }

  public getMessages() {
    return this.http.get<{ html: string }[]>(`${this.apiUrl}/messages`).pipe(
      map((result) => result[0].html?.trim()),
      catchError((error) => {
        if (error.status === 404) {
          return EMPTY;
        }
        throw error;
      }),
    );
  }

  public getAccountSelectorBackground() {
    return this.http.get<string>(`${this.apiUrl}/account-selector-background`, {
      params: getMerchandiseParams(),
    });
  }

  public getSupportPage(accountId?: string) {
    return this.http.get<[MerchandiseHTMLResponseEntry<string>]>(
      `${this.apiUrl}/support-page`,
      { params: getMerchandiseParams(accountId, null, null, false) },
    );
  }

  public getLandingPageStoryID(
    siteId: string | null,
    accountId: string,
    subSiteId: string | null,
  ) {
    const subSiteUrlPart = !!subSiteId ? `-${subSiteId}` : '';
    return this.http.get<string>(
      `${this.apiUrl}/landing-page-story${subSiteUrlPart}`,
      {
        params: getMerchandiseParams(accountId, siteId),
      },
    );
  }

  /**
   * Can return HomePageContent or routes with id such as /content/pcn-landing-page
   * @param siteId
   * @param accountId
   */
  public getHomePageDecider(
    siteId: string | null,
    accountId: string,
    slotPostFix?: string | null,
  ): Observable<HomePageContent | string> {
    const slotPart = !!slotPostFix ? `-${slotPostFix}` : '';
    return this.http
      .get<[{ html: string }]>(`${this.apiUrl}/home${slotPart}`, {
        params: getMerchandiseParams(accountId, siteId),
      })
      .pipe(
        map(
          (result) =>
            (
              result[0].html ?? HomePageContent.LANDING_PAGE
            ).trim() as HomePageContent,
        ),
      );
  }

  // TODO parameters can be removed in future as they are in headers
  public getLandingPage(
    siteId: string | null,
    subSiteId: string | null,
    accountId: string,
    slotPostFix?: string | null,
    version?: string | null,
  ) {
    const subSiteUrlPart = !!subSiteId ? `-${subSiteId}` : '';
    const slotPart = !!slotPostFix ? `-${slotPostFix}` : '';
    return this.http
      .get<[MerchandiseHTMLResponseEntry<ILandingPageMerchandise>]>(
        `${this.apiUrl}/landing-page${slotPart}${subSiteUrlPart}`,
        {
          params: getMerchandiseParams(accountId, siteId, version),
          headers: DEFAULT_HEADERS_PARAMS,
        },
      )
      .pipe(map((result) => result[0].html));
  }

  public getLandingPagePanel(
    siteId: string | null,
    subSiteId: string | null,
    accountId: string,
    slotPostFix?: string | null,
    version?: string | null,
  ) {
    const subSiteUrlPart = !!subSiteId ? `-${subSiteId}` : '';
    const slotPart = !!slotPostFix ? `-${slotPostFix}` : '';
    return this.http
      .get<[MerchandiseHTMLResponseEntry<ILandingPageMerchandise>]>(
        `${this.apiUrl}/landing-page-panel${slotPart}${subSiteUrlPart}`,
        {
          params: getMerchandiseParams(accountId, siteId, version),
          headers: DEFAULT_HEADERS_PARAMS,
        },
      )
      .pipe(map((result) => result[0].html));
  }

  public getPanelSlot<T>(
    slot: string,
    siteId: string | null,
    subSiteId: string | null,
    accountId: string | null,
    slotPostFix?: string | null,
  ) {
    const subSiteUrlPart = !!subSiteId ? `-${subSiteId}` : '';
    const slotPart = !!slotPostFix ? `-${slotPostFix}` : '';
    const slotWithPcnPrefix = `epc-panels-${slot.toLowerCase()}`;

    return this.http
      .get<[MerchandiseHTMLResponseEntry<T>]>(
        `${this.apiUrl}/${slotWithPcnPrefix}${subSiteUrlPart}${slotPart}`,
        { params: getMerchandiseParams(accountId, siteId) },
      )
      .pipe(
        map((result) => result[0].html),
        catchError(() => of(null)),
      );
  }

  public getSecondaryNavigation(
    siteId: string,
    accountId: string,
    subSiteId: string | null,
  ) {
    // -sub is required and communicated with client
    const subSiteUrlPart = !!subSiteId ? `-sub-${subSiteId}` : '';

    return this.http
      .get<[MerchandiseHTMLResponseEntry<ISecondaryNavigationBarConfig>]>(
        `${this.apiUrl}/secondary-navigation-bar${subSiteUrlPart}`,
        {
          params: getMerchandiseParams(accountId, siteId),
          headers: DEFAULT_HEADERS_PARAMS,
        },
      )
      .pipe(
        map((result) => result[0].html),
        map((resp) => {
          // tslint:disable-next-line:no-any
          const val = resp.showBurgerMenuOnMobile as any as string;
          return {
            ...resp,
            showBurgerMenuOnMobile: val?.toLocaleLowerCase() === 'true',
          };
        }),
      );
  }

  public getInformationBanner(
    siteId: string,
    accountId: string,
    slotPostFix?: string | null,
  ) {
    const slotPart = !!slotPostFix ? `-${slotPostFix}` : '';
    return this.http
      .get<[MerchandiseHTMLResponseEntry<IInformationBannerConfiguration>]>(
        `${this.apiUrl}/banner${slotPart}`,
        {
          params: getMerchandiseParams(accountId, siteId),
          headers: DEFAULT_HEADERS_PARAMS,
        },
      )
      .pipe(map((result) => result[0].html));
  }

  public getLiveStream(
    siteId: string | null,
    subSiteId: string | null,
    accountId: string,
    version?: string | null,
  ) {
    const subSiteUrlPart = !!subSiteId ? `-${subSiteId}` : '';
    return this.http
      .get<[MerchandiseHTMLResponseEntry<ILiveStreamConfiguration>]>(
        `${this.apiUrl}/live-stream${subSiteUrlPart}`,
        {
          params: getMerchandiseParams(accountId, siteId, version),
          headers: DEFAULT_HEADERS_PARAMS,
        },
      )
      .pipe(map((result) => result[0].html));
  }

  public getBuilderContentApiKey(siteId: string, accountId: string) {
    return this.http
      .get<[MerchandiseHTMLResponseEntry<string>]>(
        `${this.apiUrl}/builder-api-key`,
        {
          params: getMerchandiseParams(accountId, siteId),
          headers: DEFAULT_HEADERS_PARAMS,
        },
      )
      .pipe(map((result) => result[0].html));
  }

  public getBuilderContentTemplateId(siteId: string, accountId: string) {
    return this.http
      .get<[MerchandiseHTMLResponseEntry<string>]>(
        `${this.apiUrl}/builder-landing`,
        {
          params: getMerchandiseParams(accountId, siteId),
          headers: DEFAULT_HEADERS_PARAMS,
        },
      )
      .pipe(map((result) => result[0].html));
  }

  public getIntercom(siteId: string | null, accountId?: string) {
    return this.http.get<string>(`${this.apiUrl}/intercom`, {
      params: getMerchandiseParams(accountId, siteId),
      headers: DEFAULT_HEADERS_PARAMS,
    });
  }

  public getPulseContent(
    slot: string,
    siteId: string | null,
    subSiteId: string | null,
    accountId: string,
    slotPostFix?: string | null,
  ) {
    const subSiteUrlPart = !!subSiteId ? `-${subSiteId}` : '';
    const slotPart = !!slotPostFix ? `-${slotPostFix}` : '';
    const slotWithPcnPrefix = `pcn-${slot.replace('pcn-', '')}`;
    const dontUseJSON = false;

    return this.http
      .get<[MerchandiseHTMLResponseEntry<string>]>(
        `${this.apiUrl}/${slotWithPcnPrefix}${subSiteUrlPart}${slotPart}`,
        { params: getMerchandiseParams(accountId, siteId, null, dontUseJSON) },
      )
      .pipe(map((result) => result[0].html));
  }

  public getFooter(siteId: string | null, accountId: string) {
    return this.http
      .get<[MerchandiseHTMLResponseEntry<IFooter>]>(`${this.apiUrl}/footer`, {
        params: getMerchandiseParams(accountId, siteId),
      })
      .pipe(map((result) => result[0].html));
  }

  public getSiteMerchandisingMedia(
    context: MerchandisingContext,
    siteId: string,
    accountId: string,
    start: number = START_FROM,
    num: number = PAGE_SIZE,
  ): Observable<MediaAsset[]> {
    const httpParams = getMerchandiseParams(accountId, siteId)
      .set('start', `${start}`)
      .set('num', `${num}`);

    return this.http
      .get(`${this.apiUrl}/${context}`, { params: httpParams })
      .pipe(
        concatMap((assets) => {
          let textAssets = assets as TextAsset[];
          textAssets = textAssets.filter((asset) => 'text' in asset);
          if (!textAssets || !textAssets.length) {
            return of([]);
          }
          const firstAsset = textAssets[0];
          const endPoint = firstAsset.text;
          return this.http.get<IFileDirectory>(endPoint).pipe(
            map((directory) => {
              return directory.files
                .filter(
                  (file: IFile) =>
                    file.contentType?.startsWith('image/') ||
                    file.contentType?.startsWith('video/'),
                )
                .map(
                  (file: IFile) =>
                    ({
                      name: file.name,
                      url: file.cdnUrl || file.url,
                      contentType: file.contentType,
                      mediaType: file.mediaType,
                      subclipUrl:
                        (file?.processedVideoInfo?.subclipUrl?.length ?? 0) > 0
                          ? file?.processedVideoInfo?.subclipUrl
                          : null,
                    } as MediaAsset),
                );
            }),
          );
        }),
      );
  }

  /** this is deprecated API and used only for ECCO. */
  public getMerchandisePrices(
    accountId: string,
    siteId: string,
  ): Observable<MerchandisePrices[]> {
    return this.http
      .post<MerchandisePrices[]>(this.apiUrl, {
        requests: [...Array(100).keys()].map((idx) => ({
          // idx + 1 since discounts start from 1, not from 0 up until 100.
          slotId: `campaign-products-${idx + 1}`,
          context: {
            accountId,
            start: 0,
            num: 1000,
            siteId,
          },
        })),
      })
      .pipe(catchError(() => of([])));
  }

  public getMarketingProducts(
    accountId: string,
    siteId: string,
  ): Observable<MerchandiseMarketProduct[]> {
    return this.http
      .post<{ value: { products: Product[] } }[]>(this.apiUrl, {
        requests: [
          {
            slotId: MerchandiseSlotId.MarketingLabelProducts,
            context: {
              accountId,
              start: 0,
              num: 1000,
              siteId,
            },
          },
        ],
      })
      .pipe(
        map((result) =>
          result[0]?.value?.products ? result[0].value.products : [],
        ),
        map((products) =>
          products.map(({ productId, displayedProductId, title, brandId }) => ({
            productId,
            displayedProductId,
            title,
            brandId,
          })),
        ),
        catchError(() => EMPTY),
      );
  }

  public parseResponse(value: string): unknown {
    if (value && value.match(/^[[{]/)) {
      try {
        return JSON.parse(value.replace(/\n/gm, ''));
      } catch {
        return null;
      }
    }

    return yaml.load(value);
  }
}

function getMerchandiseParams(
  accountId?: string | null,
  siteId?: string | null,
  version?: string | null,
  formatJson = true,
): HttpParams {
  let params = new HttpParams();
  if (accountId) params = params.append('accountId', accountId);
  if (siteId) params = params.append('site', siteId);
  if (version) params = params.append('version', version);
  if (formatJson) params = params.append('format', 'json');
  return params;
}
